import { formatMinute } from "./journal-time"

/**
 * Свободные окна переноса, разложенные по частям дня (5-А3).
 *
 * ПОЧЕМУ НЕ ПРОСТО СПИСОК. Раньше диалог брал первые восемь окон подряд. Пока день был
 * только сегодняшним, отсчёт шёл от «сейчас», и восьми подсказок хватало. С выбором дня
 * отсчёт для завтра начинается с начала смены — и восемь окон это 09:00, 09:15 … 10:45,
 * то есть первые два часа. «Перенесите меня на завтра на вечер» становилось невыполнимым
 * в продукте: администратор отменял запись и создавал новую, теряя историю и предоплату.
 *
 * ПОЧЕМУ НЕ ВСЕ ОКНА СПИСКОМ. При шаге 5 минут смена в двенадцать часов даёт 144 чипа:
 * это не выбор, а стена. Части дня — то, как о времени говорят в трубке: «утром»,
 * «после обеда», «вечером». В каждой части показывается несколько ближайших окон,
 * а остаток честно назван числом, чтобы человек знал, что там есть ещё.
 */

export interface WindowGroup {
  readonly key: "morning" | "afternoon" | "evening"
  readonly title: string
  readonly minutes: readonly number[]
  /** Сколько окон этой части дня не поместилось. 0 — поместились все. */
  readonly more: number
}

/** Границы частей дня. Полдень и пять вечера — те же, что в разговоре у стойки. */
const NOON = 12 * 60
const EVENING = 17 * 60

/** Сколько окон показывать в каждой части дня. Больше — уже стена из чипов. */
const PER_GROUP = 6

export function groupWindows(minutes: readonly number[]): readonly WindowGroup[] {
  const groups: { key: WindowGroup["key"]; title: string; all: number[] }[] = [
    { key: "morning", title: "Утро", all: [] },
    { key: "afternoon", title: "День", all: [] },
    { key: "evening", title: "Вечер", all: [] },
  ]

  for (const minute of [...minutes].sort((a, b) => a - b)) {
    const index = minute < NOON ? 0 : minute < EVENING ? 1 : 2
    groups[index]?.all.push(minute)
  }

  return groups
    .filter((group) => group.all.length > 0)
    .map((group) => ({
      key: group.key,
      title: group.title,
      minutes: group.all.slice(0, PER_GROUP),
      more: Math.max(group.all.length - PER_GROUP, 0),
    }))
}

/** «ещё 7 окон до 20:45» — остаток называется числом и границей, а не многоточием. */
export function moreLabel(group: WindowGroup, all: readonly number[]): string {
  const last = [...all].sort((a, b) => a - b).at(-1)
  return last === undefined
    ? `ещё ${String(group.more)}`
    : `ещё ${String(group.more)} до ${formatMinute(last)}`
}
